import { CheckboxGroupInput, Create, Edit, required, Resource, TextInput, useGetOne, useRedirect } from "react-admin";
import { useWatch } from "react-hook-form";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { Column } from "../misc/Column";
import Box from "@mui/material/Box";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Row } from "../misc/Row";
import { Form2 } from "../misc/Form2";
import { grants } from "../backoffice.access_control";
import { AccessDenied } from "../misc/AccessDenied";
import { CourseModulesInput } from "../inputs/CourseModulesInput";
import { type DrivingSchool } from "../providers/drivingSchoolsProvider";
import { type CourseTemplate } from "../providers/courseTemplatesProvider";
import { ColorInput } from "../inputs/ColorInput";
import { SaveButton2 } from "../buttons/SaveButton2";
import { CloneButton2 } from "../buttons/CloneButton2";
import { DeleteRecordButton } from "../buttons/DeleteRecordButton";
import { DefaultList } from "../misc/DefaultList";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

function CreateCourseTemplateForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();
  const { data: drivingSchool } = useGetOne<DrivingSchool>(
    "drivingSchools",
    { id: drivingSchoolId! },
    { enabled: !!drivingSchoolId },
  );

  if (!grants.has("CourseTemplate:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  if (!drivingSchool) {
    return <LoadingIndicator />;
  }

  return (
    <Create
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Kursvorlage erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/courseTemplates`);
        },
        onError: (error: Error) => {
          handleError("Fehler beim Erstellen der Kursvorlage.", error);
        },
      }}
      transform={(data: Omit<CourseTemplate, "drivingSchoolId">): CourseTemplate => ({
        ...data,
        drivingSchoolId,
      })}
    >
      <CourseTemplateForm mode="create" />
    </Create>
  );
}

function EditCourseTemplateForm() {
  const courseTemplate = useRecordAndSwitchDrivingSchoolIfNecessary<CourseTemplate>((it) => it.drivingSchoolId);
  const redirect = useRedirect();

  if (!grants.has("CourseTemplate:edit")) {
    return <AccessDenied />;
  }

  if (!courseTemplate) {
    return <LoadingIndicator />;
  }

  return (
    <Edit
      title={
        <BackofficeRecordTitle
          header={
            <RecordDetailsHeader
              backLabel="Kursvorlagen"
              backUrl={`/drivingSchools/${courseTemplate.drivingSchoolId}/courseTemplates`}
              title={courseTemplate.name}
            />
          }
        />
      }
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert.");
          redirect(`/drivingSchools/${courseTemplate.drivingSchoolId}/courseTemplates`);
        },
        onError: (error: unknown) => {
          handleError("Fehler beim Speichern.", error);
        },
      }}
    >
      <CourseTemplateForm mode="edit" />
    </Edit>
  );
}

function CourseTemplateForm(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  const [{ drivingSchoolId }] = useAutovioContext();
  return (
    <Form2 warnWhenUnsavedChanges>
      <Column sx={{ padding: overviewCardStyle.padding }}>
        <TextInput
          label="Kursname"
          source="name"
          autoFocus={mode === "create"}
          validate={required("Bitte gib den Namen des Kurses hier ein.")}
        />
        <CheckboxGroupInput
          label="Führerscheinklassen, die durch diesen Kurs erworben werden"
          source="drivingLicenseClasses"
          choices={["B", "BE", "C1", "C1E", "C", "CE", "D1", "D1E", "D", "DE"].map((value) => ({
            id: value,
            name: value,
          }))}
          defaultValue={[]}
        />
        <ColorInput
          label="Kursfarbe"
          source="color"
          defaultValue="#ffff00"
          validate={required("Bitte wähle die Farbe aus, in der der Kurs im Kalender angezeigt werden soll.")}
          size="small"
        />
        <_CourseModulesInput />
        <Row
          sx={{
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            justifyContent: "space-between",
            pt: 1,
          }}
        >
          <SaveButton2 sx={{ width: { xs: "100%", sm: "auto" } }} />
          {mode === "edit" && (
            <Row
              sx={{
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                "& > .MuiButton-root": { flex: { xs: 1, sm: "initial" }, minWidth: 0 },
              }}
            >
              <CloneButton2 />
              <DeleteRecordButton onSuccessRedirectTo={`/drivingSchools/${drivingSchoolId}/courseTemplates`} />
            </Row>
          )}
        </Row>
      </Column>
    </Form2>
  );
}

function _CourseModulesInput() {
  const courseColor = useWatch<{ color: string }>({ name: "color" });
  return <CourseModulesInput label="Kursteile" source="modules" defaultColor={courseColor} isRequired />;
}

export const courseTemplatesResource = (
  <Resource
    key="courseTemplates"
    name="courseTemplates"
    list={DefaultList}
    create={CreateCourseTemplateForm}
    edit={EditCourseTemplateForm}
    options={{
      label: "Kursvorlagen",
      createTitle: "Neue Kursvorlage",
      noEntriesText: "Noch keine Kursvorlage hinterlegt.",
      noMatchingEntriesText: "Keine passenden Kursvorlagen gefunden.",
    }}
    recordRepresentation={(courseTemplate: CourseTemplate) => courseTemplate.name}
  />
);
